import React, { useState, useCallback, useMemo } from 'react';
import { MapNode } from './useMapLayout';
import { FiChevronRight, FiChevronDown, FiTarget, FiEye } from 'react-icons/fi';

interface Props {
  nodes: MapNode[];
  selectedNodeId: string | null;
  needsRecompile: boolean;
  darkMode: boolean;
  onFlyTo: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
}

// ── Cluster metadata ──────────────────────────────────────────────────────────
const CLUSTER_META: Record<string, { icon: string; label: string; order: number }> = {
  IDENTITY_CORE: { icon: '🧬', label: 'Identity Core', order: 0 },
  TUNING:        { icon: '🎛',  label: 'Tuning',        order: 1 },
  ARTIFACT:      { icon: '📄', label: 'Artifact',       order: 2 },
  PROBES:        { icon: '🔬', label: 'Probes',         order: 3 },
  CAPABILITIES:  { icon: '⚡', label: 'Capabilities',   order: 4 },
  LINEAGE:       { icon: '🔗', label: 'Lineage',        order: 5 },
};

const NODE_ICONS: Record<string, string> = {
  identity_cluster:   '🧬',
  character_bible:    '📖',
  tethic:             '⚡',
  pattern:            '✦',
  antipattern:        '⊘',
  controls_cluster:   '🎛',
  ui_control:         '━',
  system_prompt:      '⬡',
  controller_prompt:  '⚙',
  probe_cluster:      '🔬',
  probe:              '●',
  capability_cluster: '⚡',
  capability:         '◆',
  lineage_cluster:    '🔗',
  tag:                '🏷',
  suggested_name:     '✨',
  origin_id:          '🔁',
};

// ── Build tree items from flat nodes ─────────────────────────────────────────
interface TreeItem {
  id: string;
  nodeId: string;
  label: string;
  icon: string;
  clusterId: string;
  isCluster: boolean;
  isStale?: boolean;
  passState?: 'pass' | 'fail' | 'untested';
  children: TreeItem[];
  depth: number;
}

function buildTree(nodes: MapNode[], needsRecompile: boolean): TreeItem[] {
  const clusterMap = new Map<string, TreeItem>();

  Object.entries(CLUSTER_META)
    .sort((a, b) => a[1].order - b[1].order)
    .forEach(([clusterId, meta]) => {
      clusterMap.set(clusterId, {
        id: `cluster_${clusterId}`,
        nodeId: '',
        label: meta.label,
        icon: meta.icon,
        clusterId,
        isCluster: true,
        children: [],
        depth: 0,
      });
    });

  nodes.forEach((node) => {
    const clusterItem = clusterMap.get(node.clusterId);
    if (!clusterItem) return;

    const isStale = node.type === 'system_prompt' && needsRecompile;

    const item: TreeItem = {
      id: `node_${node.id}`,
      nodeId: node.id,
      label: node.label,
      icon: NODE_ICONS[node.type] ?? '●',
      clusterId: node.clusterId,
      isCluster: false,
      isStale,
      children: [],
      depth: 1,
    };

    // Expand inner children for cluster nodes
    if (node.type === 'identity_cluster' && node.innerIdentity?.length) {
      const pillarGroups: Record<string, TreeItem[]> = {
        character_bible: [],
        tethic: [],
        pattern: [],
        antipattern: [],
      };
      node.innerIdentity.forEach((inner) => {
        pillarGroups[inner.pillar]?.push({
          id: `inner_${inner.id}`,
          nodeId: node.id,
          label: inner.label,
          icon: NODE_ICONS[inner.pillar] ?? '●',
          clusterId: node.clusterId,
          isCluster: false,
          children: [],
          depth: 3,
        });
      });
      Object.entries(pillarGroups).forEach(([pillar, items]) => {
        if (!items.length) return;
        item.children.push({
          id: `pillar_${pillar}_${node.id}`,
          nodeId: node.id,
          label: `${pillar.replace(/_/g, ' ')} (${items.length})`,
          icon: NODE_ICONS[pillar] ?? '●',
          clusterId: node.clusterId,
          isCluster: false,
          children: items,
          depth: 2,
        });
      });
    }

    if (node.type === 'probe_cluster' && node.innerProbes?.length) {
      node.innerProbes.forEach((inner) => {
        const probe = inner.data;
        item.children.push({
          id: `inner_${inner.id}`,
          nodeId: node.id,
          label: inner.label,
          icon: probe.last_passed === true ? '✓' : probe.last_passed === false ? '✗' : '○',
          clusterId: node.clusterId,
          isCluster: false,
          passState: probe.last_passed === true ? 'pass' : probe.last_passed === false ? 'fail' : 'untested',
          children: [],
          depth: 2,
        });
      });
    }

    if (node.type === 'capability_cluster' && node.innerCapabilities?.length) {
      node.innerCapabilities.forEach((inner) => {
        item.children.push({
          id: `inner_${inner.id}`,
          nodeId: node.id,
          label: `${inner.label} (${inner.data.score}/10)`,
          icon: '◆',
          clusterId: node.clusterId,
          isCluster: false,
          children: [],
          depth: 2,
        });
      });
    }

    if (node.type === 'controls_cluster' && node.innerControls?.length) {
      node.innerControls.forEach((inner) => {
        const ctrl = inner.data;
        const isNonDefault = ctrl.current_value !== ctrl.default_value;
        item.children.push({
          id: `inner_${inner.id}`,
          nodeId: node.id,
          label: inner.label,
          icon: isNonDefault ? '●' : '○',
          clusterId: node.clusterId,
          isCluster: false,
          children: [],
          depth: 2,
        });
      });
    }

    if (node.type === 'lineage_cluster' && node.innerLineage?.length) {
      node.innerLineage.forEach((inner) => {
        item.children.push({
          id: `inner_${inner.id}`,
          nodeId: node.id,
          label: inner.label,
          icon: NODE_ICONS[inner.section] ?? '●',
          clusterId: node.clusterId,
          isCluster: false,
          children: [],
          depth: 2,
        });
      });
    }

    clusterItem.children.push(item);
  });

  return [...clusterMap.values()].filter((c) => c.children.length > 0);
}

// ── Tree row ──────────────────────────────────────────────────────────────────
interface TreeRowProps {
  item: TreeItem;
  isExpanded: boolean;
  isSelected: boolean;
  darkMode: boolean;
  onToggle: () => void;
  onFlyTo: () => void;
  onSelect: () => void;
}

const TreeRow: React.FC<TreeRowProps> = ({
  item, isExpanded, isSelected, darkMode, onToggle, onFlyTo, onSelect,
}) => {
  const hasChildren = item.children.length > 0;
  const indent = item.depth * 12;

  const passColor =
    item.passState === 'pass'     ? 'text-green-400' :
    item.passState === 'fail'     ? 'text-red-400'   :
    item.passState === 'untested' ? 'text-gray-500'  : '';

  return (
    <div
      className={`flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer select-none group transition-colors ${
        isSelected
          ? darkMode ? 'bg-blue-800/60 text-blue-200' : 'bg-blue-100 text-blue-800'
          : darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
      } ${item.isStale ? '!text-amber-400' : ''}`}
      style={{ paddingLeft: indent + 8 }}
      onClick={item.isCluster ? onToggle : onSelect}
    >
      {/* Expand/collapse chevron */}
      <span className="flex-shrink-0 w-3 h-3 flex items-center justify-center">
        {hasChildren ? (
          isExpanded
            ? <FiChevronDown size={10} />
            : <FiChevronRight size={10} />
        ) : (
          <span className="w-3" />
        )}
      </span>

      {/* Icon */}
      <span className={`flex-shrink-0 ${passColor}`} style={{ fontSize: 10 }}>
        {item.icon}
      </span>

      {/* Label */}
      <span className={`flex-1 truncate leading-tight ${
        item.isCluster ? 'font-semibold' : ''
      }`} style={{ fontSize: 10 }}>
        {item.label}
        {item.isStale && <span className="ml-1 text-amber-400">⚠</span>}
      </span>

      {/* Fly-to button — only on non-cluster nodes with a real nodeId */}
      {!item.isCluster && item.nodeId && (
        <button
          onClick={(e) => { e.stopPropagation(); onFlyTo(); }}
          className={`flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded transition-opacity ${
            darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
          }`}
          title="Fly to node"
        >
          <FiTarget size={9} />
        </button>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const MapTreeBrowser: React.FC<Props> = ({
  nodes, selectedNodeId, needsRecompile, darkMode, onFlyTo, onSelect,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(
    Object.keys(CLUSTER_META).map((id) => `cluster_${id}`)
  ));
  const [search, setSearch] = useState('');

  const tree = useMemo(
    () => buildTree(nodes, needsRecompile),
    [nodes, needsRecompile],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Flatten tree for rendering (respects expand state + search filter)
  const flatItems = useMemo(() => {
    const result: TreeItem[] = [];
    const lowerSearch = search.toLowerCase();

    const walk = (items: TreeItem[]) => {
      items.forEach((item) => {
        const matchesSearch = !search ||
          item.label.toLowerCase().includes(lowerSearch);

        if (matchesSearch || !search) {
          result.push(item);
        }

        const isExpanded = expandedIds.has(item.id) || !!search;
        if (isExpanded && item.children.length) {
          walk(item.children);
        }
      });
    };

    walk(tree);
    return result;
  }, [tree, expandedIds, search]);

  if (collapsed) {
    return (
      <div
        className={`absolute left-2 top-2 z-20 flex flex-col items-center gap-1 p-1.5 rounded-lg border shadow-lg ${
          darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'
        }`}
      >
        <button
          onClick={() => setCollapsed(false)}
          className={`p-1.5 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          title="Expand tree browser"
        >
          <FiEye size={14} />
        </button>
        <span
          className="text-xs font-bold opacity-50"
          style={{ writingMode: 'vertical-rl', fontSize: 9, letterSpacing: 1 }}
        >
          TREE
        </span>
      </div>
    );
  }

  return (
    <div
      className={`absolute left-2 top-2 z-20 flex flex-col rounded-lg border shadow-xl overflow-hidden ${
        darkMode ? 'bg-gray-900/97 border-gray-700' : 'bg-white/97 border-gray-200'
      }`}
      style={{ width: 200, maxHeight: 'calc(100% - 16px)' }}
    >
      {/* Header */}
      <div className={`flex items-center gap-1.5 px-2 py-1.5 border-b flex-shrink-0 ${
        darkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'
      }`}>
        <span className="text-xs font-bold uppercase tracking-wide opacity-60" style={{ fontSize: 9 }}>
          Map Tree
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setCollapsed(true)}
          className={`p-0.5 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
          title="Collapse"
        >
          <FiEye size={11} />
        </button>
      </div>

      {/* Search */}
      <div className={`px-2 py-1 border-b flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter…"
          className={`w-full px-1.5 py-0.5 rounded outline-none ${
            darkMode
              ? 'bg-gray-800 text-gray-300 placeholder-gray-600 border border-gray-700'
              : 'bg-gray-100 text-gray-700 placeholder-gray-400 border border-gray-200'
          }`}
          style={{ fontSize: 10 }}
        />
      </div>

      {/* Tree rows */}
      <div className="flex-1 overflow-y-auto py-1" style={{ minHeight: 0 }}>
        {flatItems.length === 0 ? (
          <div className="px-3 py-4 text-center opacity-50" style={{ fontSize: 10 }}>
            {search ? 'No matches' : 'No nodes yet'}
          </div>
        ) : (
          flatItems.map((item) => (
            <TreeRow
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              isSelected={selectedNodeId === item.nodeId && !!item.nodeId}
              darkMode={darkMode}
              onToggle={() => toggleExpand(item.id)}
              onFlyTo={() => item.nodeId && onFlyTo(item.nodeId)}
              onSelect={() => {
                if (item.nodeId) {
                  onSelect(item.nodeId);
                  onFlyTo(item.nodeId);
                } else {
                  toggleExpand(item.id);
                }
              }}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className={`px-2 py-1 border-t flex-shrink-0 text-center ${
        darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-100 text-gray-400'
      }`} style={{ fontSize: 9 }}>
        {nodes.length} node{nodes.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default MapTreeBrowser;
